import { Alert, AlertDescription, Button, Input, Label } from '@sitewise/ui';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';


import { resetPassword } from '../../api/auth.js';
import { ApiError } from '../../lib/api-client.js';

import { AuthShell } from './auth-shell.js';

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => resetPassword(token, password),
    onSuccess: () => {
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    },
  });

  if (!token) {
    return (
      <AuthShell title="Invalid link">
        <Alert variant="danger">
          <AlertDescription>
            This password reset link is missing required parameters. Request a new link from
            the login page.
          </AlertDescription>
        </Alert>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Choose a new password"
      description="Use at least 12 characters, mixing upper- and lowercase letters and digits."
      footer={
        <Link to="/login" className="text-fg hover:underline">
          Back to sign in
        </Link>
      }
    >
      {mutation.isSuccess ? (
        <Alert variant="success">
          <AlertDescription>
            Password updated. Redirecting to sign-in…
          </AlertDescription>
        </Alert>
      ) : (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (password !== confirm) {
              setValidationError('Passwords do not match');
              return;
            }
            setValidationError(null);
            mutation.mutate();
          }}
          noValidate
        >
          <div className="space-y-1.5">
            <Label htmlFor="password">New password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm">Confirm password</Label>
            <Input
              id="confirm"
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>
          {validationError ? (
            <Alert variant="danger">
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          ) : null}
          {mutation.error ? (
            <Alert variant="danger">
              <AlertDescription>
                {mutation.error instanceof ApiError
                  ? mutation.error.message
                  : 'Could not update password.'}
              </AlertDescription>
            </Alert>
          ) : null}
          <Button type="submit" className="w-full" disabled={mutation.isPending}>
            {mutation.isPending ? 'Updating…' : 'Update password'}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
